import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentApi } from '../api/api';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Wait for auth to finish loading
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate(`/login?redirect=/payment/success?session_id=${sessionId}`);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const checkPaymentStatus = async () => {
      if (cancelled) return;
      try {
        const result = await paymentApi.getCheckoutStatus(sessionId, true);
        if (cancelled) return;
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(result);
          await refreshUser();
        } else if (attempts < 10) {
          attempts++;
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to check payment status:', error);
        if (attempts < 10) {
          attempts++;
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
    return () => { cancelled = true; };
  }, [sessionId, isAuthenticated, authLoading]);

  return (
    <div className="min-h-screen bg-[#16162a] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#6D4AFF] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-4">Processing Payment</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Payment Successful!</h1>
            <p className="text-gray-400 mb-8">
              Thank you for subscribing to {paymentDetails?.metadata?.plan_name || 'MyPatriotMail'}. 
              Your account has been upgraded.
            </p>
            <Button 
              onClick={() => window.location.href = '/inbox'}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9] rounded-full px-8 py-6 text-lg font-bold shadow-[0_0_20px_rgba(109,74,255,0.3)] hover:shadow-[0_0_30px_rgba(109,74,255,0.5)] transition-all hover:scale-105 group"
            >
              Go to Inbox
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Payment Issue</h1>
            <p className="text-gray-400 mb-8">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-4">
              <Button 
                onClick={() => navigate('/#pricing')}
                className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] rounded-full py-6 font-bold"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/inbox')}
                variant="outline"
                className="w-full border-[#2d2d44] text-gray-300 hover:bg-[#2d2d44] rounded-full py-6"
              >
                Go to Inbox
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
